import { canv } from "@/canv/global";
import { Group } from "@/group";
import { VirtualCursor } from "@/multiManager/VirtualCursor";

interface SelectedProps {
    startX: number;
    startY: number;
    width: number;
    height: number;
}

interface MultiUserIdentityData {
    name?: string;
    token?: string;
    clientUID?: string;
    accountID?: number;
}

export class MultiUser {
    name: string;
    sessionUID: string;
    token?: string;
    clientUID?: string;
    accountID?: number;
    color: string;
    virtualCursor: VirtualCursor;
    viewData: Record<string, any> = {};

    selectedGroups: any[] = [];
    selectedGroupsStartX = 0;
    selectedGroupsStartY = 0;
    selectedGroupsWidth = 0;
    selectedGroupsHeight = 0;

    constructor(name: string, sessionUID: string, token: string | undefined, color: string) {
        this.name = name === "" ? "Guest" : name;
        this.sessionUID = sessionUID;
        this.token = token;
        this.color = color;
        this.virtualCursor = new VirtualCursor(this.name, this.color);
    }

    updateIdentityData(data: MultiUserIdentityData): void {
        if (data.name !== undefined) {
            this.name = data.name === "" ? "Guest" : data.name;
            this.virtualCursor.name = this.name;
        }

        if (data.token !== undefined) {
            this.token = data.token;
        }

        if (data.clientUID !== undefined) {
            this.clientUID = data.clientUID;
        }

        if (data.accountID !== undefined) {
            this.accountID = data.accountID;
        }
    }

    updateVirtualCursor(x: number, y: number): void {
        this.virtualCursor.updatePosition(x, y);
    }

    changeVisibility(isVisible: boolean): void {
        isVisible ? this.virtualCursor.show() : this.virtualCursor.hide();
    }

    updateViewData(data: Record<string, any>): void {
        this.viewData = data;
    }

    hasViewData(): boolean {
        return (
            typeof this.viewData.viewportStartX === "number" &&
            typeof this.viewData.viewportStartY === "number" &&
            typeof this.viewData.viewportWidth === "number" &&
            typeof this.viewData.viewportHeight === "number"
        );
    }

    setSelectedGroups(selectedGroupsUIDs: string[]): void {
        this.selectedGroups = [];

        for (const uid of selectedGroupsUIDs) {
            const item = canv.canvasController.FindByUID(uid);
            if (!item || !(item instanceof Group)) continue;

            const child = item.onlyChild();

            if (
                item.isItemWrapper() &&
                (child?.constructorName === "mindMap" || child?.constructorName === "table")
            ) {
                continue;
            }

            this.selectedGroups.push(item);
        }

        this.calcOutline();
    }

    draw(): void {
        this.calcOutline();
        this.showOutline();
        this.virtualCursor.draw();
    }

    private calcOutline(): void {
        if (this.selectedGroups.length === 0) return;

        const dims: SelectedProps = canv.canvasController.getSelectedProps(this.selectedGroups);
        this.selectedGroupsStartX = dims.startX;
        this.selectedGroupsStartY = dims.startY;
        this.selectedGroupsWidth = dims.width;
        this.selectedGroupsHeight = dims.height;
    }

    private showOutline(): void {
        if (this.selectedGroups.length === 0) return;

        canv.ctx.save();
        canv.ctx.strokeStyle = this.color;
        canv.ctx.lineWidth = canv.actionManager.getZoomConstant(2.5);
        canv.ctx.strokeRect(
            this.selectedGroupsStartX - 5,
            this.selectedGroupsStartY - 5,
            this.selectedGroupsWidth + 10,
            this.selectedGroupsHeight + 10,
        );
        canv.ctx.restore();
    }
}
